// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertTemperature,
  formatValueWithUnit,
} from '../../../../../../../../plugins/general';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  TemperatureType,
} from '../../../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../../../components/EquipmentSensorList';
import { Col } from '../../../../../../../../layout/elements/Col';
import { Row } from '../../../../../../../../layout/elements/Row';

// Modules
import { ListModule } from '../../../ListModule';

// View
import { CrusherProps } from './Crusher.types';
import { Container, ListContainer } from './Crusher.style';

function Crusher({
  className,
  testId,
  unitData,
}: CrusherProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const crusherMotorItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t('view.crusher.vsi.maintenance.setpoint.crusher.crusherMotorSpecs.fullLoad')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorFullLoadAmps, 'A'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.crusherMotorSpecs.serviceFactor',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorServiceFactor, 'SF', 2),
    },
    {
      sensor: `${t('view.crusher.vsi.maintenance.setpoint.crusher.crusherMotorSpecs.runningAmps')}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorRunningAmps, 'A'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.crusherMotorSpecs.crushingAmps',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorCrushingAmps, 'A'),
    },
  ];

  const warningHornItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.warningHornSeconds.dischargeStart',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spDischargeWarningHornTime, 's'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.warningHornSeconds.crusherStop',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spCrusherMotorWarningHornTime, 's'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.warningHornSeconds.feederStart',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spFeederWarningHornTime, 's'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.warningHornSeconds.binFeederStart',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spBinFeederWarningHornTime, 's'),
    },
  ];

  const overItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.overCurrentProtection.timeAboveFlaSF1.15Description',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSf115FeedStop, 's'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.overCurrentProtection.timeAboveFlaDescription',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaFeedStop, 's'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.overCurrentProtection.timeAboveFlaSFDescription',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSfFeedStop, 's'),
    },
  ];

  const beltItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.crusherBeltSpeed.beltSlipAlarmRpm',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spBeltSlipAlarmRpm, 'RPM'),
    },
  ];

  const vfdSpeedItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.crusherVfdSpeed.crusherVfdHzMinSetpoint',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spVFDHzMinSetpoint, 'Hz'),
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.crusherVfdSpeed.crusherVfdHzMaxSetpoint',
      )}`,
      setpoint: formatValueWithUnit(unitData?.spVFDHzMaxSetpoint, 'Hz'),
    },
  ];

  const lubeReturnLineItems: EquipmentSensorListItem[] = [
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.lubeReturnLine.highAlarm',
      )}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempHighWarn,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.lubeReturnLine.stopFeedHighAlarm',
      )}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempStopFeed,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: `${t(
        'view.crusher.vsi.maintenance.setpoint.crusher.lubeReturnLine.crusherStopHighAlarm',
      )}`,
      setpoint: convertTemperature(
        unitData?.spLubeReturnTempCrusherStop,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  return (
    <Container className={className} data-testid={testId}>
      <Row>
        <ListContainer>
          <Col cols={8}>
            <ListModule
              title={`${
                t('view.crusher.vsi.maintenance.setpoint.crusher.crusherMotorSpecs.title')
              }`}
              items={crusherMotorItems}
            />
          </Col>
          <Col cols={8}>
            <ListModule
              title={`${t(
                'view.crusher.vsi.maintenance.setpoint.crusher.overCurrentProtection.title',
              )}`}
              items={overItems}
            />
          </Col>
          <Col cols={8}>
            <ListModule
              title={`${t(
                'view.crusher.vsi.maintenance.setpoint.crusher.crusherBeltSpeed.title',
              )}`}
              items={beltItems}
            />
          </Col>
        </ListContainer>
      </Row>
      <Row>
        <ListContainer>
          <Col cols={8}>
            <ListModule
              title={`${t(
                'view.crusher.vsi.maintenance.setpoint.crusher.warningHornSeconds.title',
              )}`}
              items={warningHornItems}
            />
          </Col>
          <Col cols={8}>
            <ListModule
              title={`${t(
                'view.crusher.vsi.maintenance.setpoint.crusher.crusherVfdSpeed.title',
              )}`}
              items={vfdSpeedItems}
            />
          </Col>
          <Col cols={8}>
            <ListModule
              title={`${t(
                'view.crusher.vsi.maintenance.setpoint.crusher.lubeReturnLine.title',
              )}`}
              items={lubeReturnLineItems}
            />
          </Col>
        </ListContainer>
      </Row>
    </Container>
  );
}

export { Crusher };
